import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useColorModeValue,
  Container,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Stack,
  Link as ChakraLink,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FiLock } from 'react-icons/fi';
import { LOGO_URL, APP_NAME } from '../constants/branding';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid password reset link. Please request a new one.');
    }
  }, [token]);

  const validateForm = () => {
    let hasError = false;
    
    setPasswordError('');
    setConfirmPasswordError('');
    
    if (!password) {
      setPasswordError('Password is required');
      hasError = true;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      hasError = true;
    }
    
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your password');
      hasError = true;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      hasError = true;
    }
    
    return !hasError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8889'}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: password,
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setPassword('');
        setConfirmPassword('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to reset password. The link may have expired.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirectToLogin = () => {
    navigate('/login');
  };
  
  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      {/* Background Design */}
      <Box
        position="absolute"
        top="-10%"
        right="-10%"
        width="600px"
        height="600px"
        bg="blue.500"
        borderRadius="full"
        opacity={0.1}
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-15%"
        width="800px"
        height="800px"
        bg="blue.400"
        borderRadius="full"
        opacity={0.1}
      />

      <Container maxW="sm" position="relative" zIndex={1}>
        <VStack spacing={6} bg="white" p={8} borderRadius="xl" boxShadow="lg">
          <VStack spacing={2} w="full" align="center">
            <Box mb={1}>
              <img 
                src={LOGO_URL}
                alt="Logo" 
                style={{ height: '50px', width: 'auto' }} 
              />
            </Box>
            <Heading size="lg" color="blue.600">Set New Password</Heading>
            <Text color="gray.600" fontSize="md">Create a new password for your account</Text>
          </VStack>
          
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          {isSuccess ? (
            <Stack spacing={4} w="full">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Your password has been reset successfully!
              </Alert>
              <Button colorScheme="blue" size="md" width="full" onClick={handleRedirectToLogin}>
                Back to Login
              </Button>
            </Stack>
          ) : (
            !error && (
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <VStack spacing={4} w="full">
                  <Text fontSize="sm" color="gray.600">
                    Enter your new password below.
                  </Text>
                  
                  <Box w="full">
                    <Text mb={1} fontWeight="medium" fontSize="sm" color="gray.600">
                      New Password
                    </Text>
                    <FormControl isInvalid={!!passwordError}>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your new password"
                          variant="filled"
                          size="md"
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            variant="ghost"
                            onClick={() => setShowPassword(!showPassword)}
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                      {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                    </FormControl>
                  </Box>
                  
                  <Box w="full">
                    <Text mb={1} fontWeight="medium" fontSize="sm" color="gray.600">
                      Confirm New Password
                    </Text>
                    <FormControl isInvalid={!!confirmPasswordError}>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your new password"
                          variant="filled"
                          size="md"
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                            variant="ghost"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                      {confirmPasswordError && <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>}
                    </FormControl>
                  </Box>
                  
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="md"
                    width="full"
                    isLoading={isSubmitting}
                    loadingText="Resetting..."
                    mt={2}
                  >
                    Reset Password
                  </Button>
                  
                  <Text align="center" w="full" fontSize="sm" color="gray.600">
                    Return to <ChakraLink as={Link} to="/login" color="blue.500">Login</ChakraLink>
                  </Text>
                </VStack>
              </form>
            )
          )}
        </VStack>
      </Container>
    </Flex>
  );
};

export default ResetPassword; 